import React, { useEffect, useState } from "react";

import moment from "moment";

import { filterOptionsWithSynonyms, getDatePickerDisabledRange, getEnvironment, isNullOrUndefined, useGetPracticeTillages, useTranslation } from "utils";

import { PracticeFCPO, ZERO_UUID } from "models";

import { Divider, Flex, Form, IconButton, OptGroup, Option, RangePicker, Select, Spin } from "components";

import { CALENDAR_YEAR_END, CALENDAR_YEAR_START } from "pages/app/all-field-view/constants/cropForm";
import { InputRequirementConfig, isRequiredForSaving, shouldShowInput } from "pages/app/all-field-view/utils/getInputRequirementConfig";

type TillageApplicationFormProps = {
  tillage: PracticeFCPO;
  index: number;
  onDelete: () => void;
  recommendedSeedingDate?: string;
  requirementConfig: InputRequirementConfig | null;
  optionalInputsAreShown: boolean;
};

const depthOptions = [
  { value: 0, name: "0-5 cm" },
  { value: 5, name: "5-10 cm" },
  { value: 10, name: "10-30 cm" },
  { value: 30, name: "30-60 cm" },
  { value: 60, name: "60+ cm" },
];

export const TillageApplicationForm = ({
  tillage,
  index,
  onDelete,
  recommendedSeedingDate,
  requirementConfig,
  optionalInputsAreShown,
}: TillageApplicationFormProps) => {
  const { t } = useTranslation(["allFieldView"]);
  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const TILLAGE = "tillage";
  const [pickerKey, setPickerKey] = useState(`${TILLAGE}-${index}`);
  const isAdmin = getEnvironment().admin;

  useEffect(() => {
    // Force the RangePicker to re-evaluate defaultValue by remounting it whenever recommendedSeedingDate changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.
    setPickerKey((prevState) => prevState + 1);
  }, [recommendedSeedingDate]);

  return (
    <Flex vertical className="tillage-application-form">
      <div className="form-container" data-testid="add-edit-tillage-application-form">
        {tillagePracticesQuery.isFetching ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <>
            <Divider className="divider" />
            <Form.Item name={[`${TILLAGE}-${index}`, "id"]} initialValue={tillage.id}></Form.Item>
            <Form.Item name={[`${TILLAGE}-${index}`, "type"]} initialValue={tillage.type}></Form.Item>
            <Flex justify="space-between">
              <h5>{t("tillageForm.application", { applicationNumber: index + 1 })}</h5>
              <IconButton type="tertiary" size="small" iconName="tabler/trash" onClick={onDelete} disabled={tillage.isConfirmed && !isAdmin} />
            </Flex>
            {/* @ts-expect-error need to convert to tsx */}
            <Select
              className="margin-top-200"
              dataTestId={`add-edit-tillage-form-tillage-type-selection-${index}`}
              label={t("tillageForm.tillageType")}
              name={[`${TILLAGE}-${index}`, "assignedPracticeId"]}
              initialValue={tillage.assignedPracticeId ?? null}
              value={tillage.assignedPracticeId ?? null}
              showSearch
              optionFilterProp="children"
              filterOption={filterOptionsWithSynonyms}
              rules={[{ message: "", required: true }]}
              disabled={tillage.isConfirmed && !isAdmin}
            >
              <OptGroup>
                {tillagePracticesQuery.data &&
                  tillagePracticesQuery.data
                    .filter((practice) => practice.id !== ZERO_UUID)
                    .sort((a, b) => a.name?.localeCompare(b.name))
                    .map((option, index: number) => (
                      <Option data-testid={`crop-selection-option-${index}`} key={option.id} value={option.id}>
                        {option.name}
                      </Option>
                    ))}
              </OptGroup>
            </Select>
            <Flex vertical gap="1rem" className="date-picker-container margin-top-100">
              <RangePicker
                key={pickerKey}
                dataTestId={`add-edit-tillage-form-date-range-${tillage.assignedPracticeId}-picker`}
                name={[`${TILLAGE}-${index}`, "dateRange"]}
                label={t("tillageForm.dateRange")}
                initialValue={[tillage.startDate ? moment(tillage.startDate) : null, tillage.endDate ? moment(tillage.endDate) : null]}
                defaultPickerValue={[
                  recommendedSeedingDate ? moment(recommendedSeedingDate) : null,
                  recommendedSeedingDate ? moment(recommendedSeedingDate) : null,
                ]}
                format={["DD.MM.YYYY"]}
                rules={[
                  { message: "", required: true },
                  {
                    validator: (_, value: moment.Moment[] | null) => {
                      if (!value?.[1]) {
                        return Promise.reject(new Error(t("tillageForm.errorMessages.dateRangeRequired")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                // Once submitted, the date can still be changed within the respective calendar year.
                disabledDate={(current) =>
                  !isAdmin &&
                  tillage.isConfirmed &&
                  !!tillage.year &&
                  getDatePickerDisabledRange({ current, startDate: `${CALENDAR_YEAR_START}${tillage.year}`, endDate: `${CALENDAR_YEAR_END}${tillage.year}` })
                }
              />
            </Flex>

            {/* If there is already tillage depth data provided we show the input independant from config */}
            {(shouldShowInput(requirementConfig?.tillageDepth, optionalInputsAreShown) || !isNullOrUndefined(tillage.tillageDepth)) && (
              // @ts-expect-error need to convert to tsx
              <Select
                className="margin-top-100"
                dataTestId="add-edit-tillage-form-tillage-depth-selection"
                label={t("tillageForm.tillageDepth")}
                name={[`${TILLAGE}-${index}`, "tillageDepth"]}
                initialValue={tillage.tillageDepth ?? null}
                value={tillage.tillageDepth ?? null}
                optionFilterProp="children"
                rules={[
                  {
                    required: isRequiredForSaving(requirementConfig?.tillageDepth),
                    message: t("tillageForm.errorMessages.tillageDepthRequired"),
                  },
                ]}
                disabled={tillage.isConfirmed && !isAdmin}
              >
                <OptGroup>
                  {depthOptions.map((option) => (
                    <Option data-testid={`tillage-depth-selection-option-${option.value}`} key={option.value} value={option.value}>
                      {option.name}
                    </Option>
                  ))}
                </OptGroup>
              </Select>
            )}
          </>
        )}
      </div>
    </Flex>
  );
};
